/**
 * Quality, audio, and the CRT itself.
 *
 * Every CRT slider is a 0..2 multiplier over the matching value in
 * `core/config.ts` CRT block, so 1.00 is exactly the tuned default and the
 * post-stack can apply them without a second table. `main.ts` receives the whole
 * settings object on every change and forwards it to the post stack and mixer.
 */

import { CRT, type QualityTier } from '@/core/config';
import { lastVerdict } from '@/core/bench';
import { perf } from '@/core/perf';
import { setHapticsEnabled, setHapticsReducedMotion } from '@/core/haptics';
import { el, icon, installUiTheme } from './Icon';
import type { IconName } from './Icon';
import { ModalShell } from './PauseMenu';
import { logoNode } from './Logo';

export interface CrtSettings {
  scanlines: number;
  mask: number;
  curvature: number;
  bloom: number;
  persistence: number;
  aberration: number;
  noise: number;
  vignette: number;
}

export interface SettingsValues {
  quality: QualityTier;
  audio: { master: number; music: number; sfx: number };
  crt: CrtSettings;
  /**
   * Adaptive quality. On, the tier the player picked is a ceiling the governor
   * may walk down from to hold the frame rate, and can never climb above. Off,
   * the tier is exactly what renders whatever it costs.
   */
  autoQuality: boolean;
  /** Forced on top of the OS preference, never able to turn it off. */
  reducedMotion: boolean;
  showStats: boolean;
  /**
   * The generative field behind the playfield. Off keeps its shader and target
   * uncreated entirely. The performance governor holds rung 1 over this, so it
   * is the first thing dropped under load whatever the player has chosen.
   */
  hydra: boolean;
  /**
   * Vibration for the things that happen to the defense — a leak, a boss, the
   * end of a run. Off on every desktop browser and on iOS whatever this says,
   * because neither has an API to vibrate anything; see `core/haptics.ts`.
   */
  haptics: boolean;
}

export interface SettingsState {
  open: boolean;
  values: SettingsValues;
}

export interface SettingsCallbacks {
  onChange?(values: SettingsValues): void;
  onClose?(): void;
  /**
   * Measure this machine again and start from what it measures.
   *
   * The boot probe runs once per machine and keeps its verdict, which is right
   * almost always and wrong in the two cases a player can see: they measured
   * while something else was hammering the GPU, or they have since picked a tier
   * by hand and want the measurement back. This is the way out of both.
   */
  onCalibrate?(): void;
}

export const DEFAULT_SETTINGS: SettingsValues = {
  quality: 'ultra',
  audio: { master: 0.8, music: 0.6, sfx: 0.9 },
  crt: {
    scanlines: 1, mask: 1, curvature: 1, bloom: 1,
    persistence: 1, aberration: 1, noise: 1, vignette: 1,
  },
  autoQuality: true,
  reducedMotion: false,
  showStats: false,
  hydra: true,
  haptics: true,
};

const QUALITY_TIERS: readonly QualityTier[] = ['potato', 'standard', 'ultra'];

/** Brightness marks for the tiers, then `sparkles` for the one that costs most. */
const QUALITY_ICON: Record<QualityTier, IconName> = {
  potato: 'brightness-low',
  standard: 'brightness-high',
  ultra: 'sparkles',
};

const QUALITY_LABEL: Record<QualityTier, string> = {
  potato: 'Low',
  standard: 'Mid',
  ultra: 'Ultra',
};

interface Slider {
  input: HTMLInputElement;
  readout: HTMLElement;
}

export class Settings {
  private shell = new ModalShell('settings-title');
  private values: SettingsValues = clone(DEFAULT_SETTINGS);
  private tierButtons = new Map<QualityTier, HTMLButtonElement>();
  private sliders = new Map<string, Slider>();
  private toggles = new Map<string, HTMLInputElement>();
  private mounted = false;
  private perfFrame: HTMLElement | null = null;
  private perfLadder: HTMLElement | null = null;
  private benchValue: HTMLElement | null = null;
  private perfTimer = 0;

  constructor(private readonly cb: SettingsCallbacks = {}) {}

  mount(root: HTMLElement): void {
    if (this.mounted) return;
    this.mounted = true;
    installUiTheme();
    this.shell.mount(root);
    this.shell.setTitle('Settings', 'cog');
    this.shell.onDismiss = () => this.cb.onClose?.();

    this.shell.box.append(
      // The one screen a player opens on purpose and reads top to bottom, so
      // it is where the cabinet says its own name.
      logoNode('md'),
      this.qualityGroup(),
      this.audioGroup(),
      this.crtGroup(),
      this.accessGroup(),
    );

    const close = el('button', 'btn btn--primary');
    close.type = 'button';
    close.append(icon('check', { size: 16 }), document.createTextNode('Done'));
    close.addEventListener('click', this.onClose);
    const actions = el('div', 'modal__actions');
    actions.append(close);
    this.shell.box.append(actions);
  }

  update(state: SettingsState): void {
    if (!this.mounted) return;
    this.values = clone(state.values);
    this.paint();
    this.shell.setOpen(state.open);
    // The readout only costs anything while somebody is looking at it.
    if (state.open) this.startPerfTicker();
    else this.stopPerfTicker();
  }

  /** The current values, for `main.ts` to persist. */
  get current(): SettingsValues {
    return clone(this.values);
  }

  destroy(): void {
    this.stopPerfTicker();
    this.shell.destroy();
    this.tierButtons.clear();
    this.sliders.clear();
    this.toggles.clear();
    this.perfFrame = null;
    this.perfLadder = null;
    this.benchValue = null;
    this.mounted = false;
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  private qualityGroup(): HTMLElement {
    const group = el('div', 'settings__group');
    group.append(this.groupTitle('analytics', 'Quality'));

    const field = el('div', 'field');
    field.append(this.fieldLabel('pc', 'Tier'));
    const seg = el('div', 'seg');
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute('aria-label', 'Quality tier');
    for (const tier of QUALITY_TIERS) {
      const btn = el('button', 'btn');
      btn.type = 'button';
      btn.setAttribute('role', 'radio');
      btn.dataset.tip = `${QUALITY_LABEL[tier]} — bloom levels, particle budget and pixel ratio.`;
      btn.append(icon(QUALITY_ICON[tier], { size: 12 }), document.createTextNode(QUALITY_LABEL[tier]));
      btn.addEventListener('click', () => {
        this.values.quality = tier;
        this.paint();
        this.emit();
      });
      this.tierButtons.set(tier, btn);
      seg.append(btn);
    }
    field.append(seg);
    group.append(field);
    group.append(
      this.toggle('autoQuality', 'bolt', 'Adaptive'),
      this.toggle('hydra', 'sparkles', 'Background visuals'),
    );
    group.append(this.governorReadout(), this.benchField());
    return group;
  }

  /**
   * What the boot probe decided, and the way to make it decide again.
   *
   * The tier moved on its own before the player ever opened this sheet, so the
   * segment above shows a choice they did not make. This is the sentence that
   * explains it — the measured cost of a quiet frame against what the frame
   * budget allowed — beside the button that re-runs the measurement.
   */
  private benchField(): HTMLElement {
    const field = el('div', 'field');
    field.append(this.fieldLabel('bolt', 'Measured'));
    this.benchValue = el('span', 'field__value num', '—');
    this.benchValue.style.opacity = '0.7';

    const btn = el('button', 'btn');
    btn.type = 'button';
    btn.style.marginLeft = 'auto';
    btn.dataset.tip =
      'Measures a frame at each tier and starts from what this machine can hold. '
      + 'Takes about a tenth of a second and replaces any tier picked by hand.';
    btn.append(icon('refresh', { size: 12 }), document.createTextNode('Re-measure'));
    btn.addEventListener('click', () => this.cb.onCalibrate?.());

    field.append(this.benchValue, btn);
    return field;
  }

  /**
   * What the governor is doing, in numbers.
   *
   * A quality setting that moves on its own is only tolerable if the player can
   * see it move, so this says both what it measured and what it took: median
   * and p95 frame time, and which rung of the ladder is currently held down.
   */
  private governorReadout(): HTMLElement {
    const field = el('div', 'field');
    field.append(this.fieldLabel('chart-line', 'Frame'));
    this.perfFrame = el('span', 'field__value num', '—');
    this.perfLadder = el('span', 'field__value num', '—');
    this.perfLadder.style.opacity = '0.7';
    field.append(this.perfFrame, this.perfLadder);
    return field;
  }

  /** 4Hz is enough to watch a rung move and slow enough to stay readable. */
  private readonly paintPerf = (): void => {
    if (this.perfFrame === null || this.perfLadder === null) return;
    const s = perf.snapshot();
    this.perfFrame.textContent = s.samples === 0
      ? '—'
      : `${s.medianFrameMs.toFixed(1)} / ${s.p95FrameMs.toFixed(1)} ms`;
    this.perfLadder.textContent = !s.enabled
      ? 'fixed'
      : s.rung === 0 ? 'full quality' : `−${s.rung} ${s.label}`;
    if (this.benchValue !== null) {
      const v = lastVerdict();
      this.benchValue.textContent = v === null
        ? 'not measured'
        : v.quietMs > 0
          ? `${v.quietMs.toFixed(1)} / ${v.allowanceMs.toFixed(1)} ms`
          : v.reason;
    }
  };

  private startPerfTicker(): void {
    if (this.perfTimer !== 0 || typeof window === 'undefined') return;
    this.paintPerf();
    this.perfTimer = window.setInterval(this.paintPerf, 250);
  }

  private stopPerfTicker(): void {
    if (this.perfTimer === 0) return;
    window.clearInterval(this.perfTimer);
    this.perfTimer = 0;
  }

  private audioGroup(): HTMLElement {
    const group = el('div', 'settings__group');
    group.append(this.groupTitle('sound-on', 'Audio'));
    group.append(
      this.slider('audio.master', 'sound-on', 'Master', 0, 1, 0.01, percent),
      this.slider('audio.music', 'music', 'Music', 0, 1, 0.01, percent),
      this.slider('audio.sfx', 'cassette-tape', 'Effects', 0, 1, 0.01, percent),
    );
    return group;
  }

  private crtGroup(): HTMLElement {
    const group = el('div', 'settings__group');
    group.append(this.groupTitle('retro-pc', 'Tube'));
    group.append(
      // `align-justify` is a stack of horizontal rules — the closest the set has
      // to a scanline pattern, and it reads correctly at 12px.
      this.slider('crt.scanlines', 'align-justify', 'Scanlines', 0, 2, 0.05, mult),
      this.slider('crt.mask', 'grid', 'Shadow mask', 0, 2, 0.05, mult),
      this.slider('crt.curvature', 'expand', 'Curvature', 0, 2, 0.05, mult),
      this.slider('crt.bloom', 'sun', 'Bloom', 0, 2, 0.05, mult),
      this.slider('crt.persistence', 'clock', 'Persistence', 0, 2, 0.05, mult),
      this.slider('crt.aberration', 'themes', 'Colour fringe', 0, 2, 0.05, mult),
      this.slider('crt.noise', 'cloud-fog', 'Noise', 0, 2, 0.05, mult),
      this.slider('crt.vignette', 'brightness-low', 'Vignette', 0, 2, 0.05, mult),
    );
    return group;
  }

  private accessGroup(): HTMLElement {
    const group = el('div', 'settings__group');
    group.append(this.groupTitle('eye', 'Comfort'));
    group.append(
      this.toggle('reducedMotion', 'eye-cross', 'Reduce motion'),
      // The phone with motion lines is the closest the set has to a vibration
      // mark, and it reads correctly at 12px.
      this.toggle('haptics', 'phone-ringing-high', 'Haptics'),
      this.toggle('showStats', 'chart-line', 'Show frame stats'),
    );
    return group;
  }

  private groupTitle(name: IconName, text: string): HTMLElement {
    const h = el('h3', 'panel__title');
    h.append(icon(name, { size: 12, role: 'uiAccent', glow: 0.6 }), document.createTextNode(text));
    return h;
  }

  private fieldLabel(name: IconName, text: string): HTMLElement {
    const label = el('span', 'field__label');
    label.append(icon(name, { size: 12 }), document.createTextNode(text));
    return label;
  }

  private slider(
    path: string,
    name: IconName,
    label: string,
    min: number,
    max: number,
    step: number,
    format: (v: number) => string,
  ): HTMLElement {
    const field = el('div', 'field');
    const id = `set-${path.replace('.', '-')}`;
    const labelNode = el('label', 'field__label');
    labelNode.htmlFor = id;
    labelNode.append(icon(name, { size: 12 }), document.createTextNode(label));

    const input = el('input');
    input.type = 'range';
    input.id = id;
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);

    const readout = el('span', 'field__value num', format(0));
    input.addEventListener('input', () => {
      const value = Number(input.value);
      this.write(path, value);
      readout.textContent = format(value);
      this.emit();
    });

    field.append(labelNode, input, readout);
    this.sliders.set(path, { input, readout });
    // Stashed on the element so `paint` can reformat without a second table.
    input.dataset.format = format === percent ? 'percent' : 'mult';
    return field;
  }

  private toggle(path: string, name: IconName, label: string): HTMLElement {
    const field = el('div', 'field');
    const id = `set-${path}`;
    const labelNode = el('label', 'field__label');
    labelNode.htmlFor = id;
    labelNode.append(icon(name, { size: 12 }), document.createTextNode(label));

    const input = el('input');
    input.type = 'checkbox';
    input.id = id;
    input.addEventListener('change', () => {
      this.write(path, input.checked);
      this.emit();
    });

    field.append(labelNode, input);
    this.toggles.set(path, input);
    return field;
  }

  private paint(): void {
    for (const [tier, btn] of this.tierButtons) {
      const on = this.values.quality === tier;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-checked', String(on));
    }
    for (const [path, slider] of this.sliders) {
      const value = Number(this.read(path));
      slider.input.value = String(value);
      slider.readout.textContent =
        slider.input.dataset.format === 'percent' ? percent(value) : mult(value);
    }
    for (const [path, input] of this.toggles) {
      input.checked = Boolean(this.read(path));
    }
    // `main.ts` forwards this object to the renderer and the mixer but knows
    // nothing about the governor, so the switch is honoured here. Owner `main`
    // should move it into `applySettings` when it next touches that file.
    perf.setEnabled(this.values.autoQuality);
    // Same arrangement, same reason: `main.ts` forwards this object to the
    // renderer and the mixer and knows about neither the governor nor the
    // vibration adapter, so both switches are honoured where they are read.
    setHapticsEnabled(this.values.haptics);
    setHapticsReducedMotion(this.values.reducedMotion);
    this.paintPerf();
  }

  private read(path: string): unknown {
    const [head, tail] = path.split('.') as [keyof SettingsValues, string | undefined];
    const top = this.values[head];
    if (tail === undefined) return top;
    return (top as Record<string, number>)[tail];
  }

  private write(path: string, value: number | boolean): void {
    const [head, tail] = path.split('.');
    if (tail === undefined) {
      // Both Comfort switches take effect on the click, not on the next open:
      // `main.ts` only calls `update` when the sheet is raised or dismissed, so
      // a value only painted there would sit dead until the sheet reopened.
      if (head === 'reducedMotion') {
        this.values.reducedMotion = Boolean(value);
        setHapticsReducedMotion(this.values.reducedMotion);
      } else if (head === 'haptics') {
        this.values.haptics = Boolean(value);
        setHapticsEnabled(this.values.haptics);
      } else if (head === 'showStats') this.values.showStats = Boolean(value);
      // Was missing, so the toggle painted itself on and emitted the old value:
      // the background layer only ever changed when a reload rehydrated it.
      else if (head === 'hydra') this.values.hydra = Boolean(value);
      else if (head === 'autoQuality') {
        this.values.autoQuality = Boolean(value);
        perf.setEnabled(this.values.autoQuality);
        this.paintPerf();
      }
      return;
    }
    if (head === 'audio') {
      (this.values.audio as unknown as Record<string, number>)[tail] = Number(value);
    } else if (head === 'crt') {
      (this.values.crt as unknown as Record<string, number>)[tail] = Number(value);
    }
  }

  private emit(): void {
    this.cb.onChange?.(clone(this.values));
  }

  private readonly onClose = (): void => {
    this.cb.onClose?.();
  };
}

/**
 * Resolves a CRT slider against the tuned defaults. `main.ts` calls this rather
 * than reimplementing the multiplication in the post stack wiring.
 */
export function resolveCrt(values: CrtSettings): {
  scanlineStrength: number;
  maskStrength: number;
  curvatureX: number;
  curvatureY: number;
  bloomIntensity: number;
  persistence: number;
  chromaticAberration: number;
  noise: number;
  vignette: number;
} {
  return {
    scanlineStrength: CRT.scanlineStrength * values.scanlines,
    maskStrength: CRT.maskStrength * values.mask,
    curvatureX: CRT.curvatureX * values.curvature,
    curvatureY: CRT.curvatureY * values.curvature,
    bloomIntensity: values.bloom,
    persistence: CRT.persistence * values.persistence,
    chromaticAberration: CRT.chromaticAberration * values.aberration,
    noise: CRT.noise * values.noise,
    vignette: CRT.vignette * values.vignette,
  };
}

function percent(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function mult(v: number): string {
  return `${v.toFixed(2)}×`;
}

function clone(v: SettingsValues): SettingsValues {
  return {
    quality: v.quality,
    audio: { ...v.audio },
    crt: { ...v.crt },
    // Defaults on when absent: an older persisted blob has no opinion about a
    // switch that did not exist when it was written, and auto is the default.
    autoQuality: v.autoQuality !== false,
    reducedMotion: v.reducedMotion,
    showStats: v.showStats,
    // Same reasoning as `autoQuality`: absent in an older persisted blob means
    // no opinion, and the default is on.
    hydra: v.hydra !== false,
    haptics: v.haptics !== false,
  };
}
